import { useCallback, useEffect, useState, memo } from "react";
import {
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Pagination,
  Select,
  TableContainer,
  TextField,
  Tooltip,
} from "@mui/material";
import { useQuery } from "react-query";
import { getAllPhoneBook } from "../../api/phone-book-api/getAllPhoneBook";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { api } from "../../api/Intercepter";
import ClientsAutoComplete from "./components/ClientsAutoComplete";
import { CustomTextField } from "../../components/inputs/input/BootStrapInput";
import ManIcon from "@mui/icons-material/Man";
import WomanIcon from "@mui/icons-material/Woman";
import { formatDateAndTime } from "../../utils/formatDateAndTime";
import { convertArabicToEnglishNumber } from "../../utils/convertArabicToEnglish";
import HelpCenterIcon from "@mui/icons-material/HelpCenter";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SendIcon from "@mui/icons-material/Send";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import { debounce } from "lodash";
import { useSnackbar } from "notistack";
import { useMutation } from "react-query";
import AddParticipationModal from "../../components/add-participation-modal/add-participation-modal";

const getFilteredPhones = (phones?: any[]) => {
  if (!phones || !Array.isArray(phones) || phones.length === 0) return [];
  const uniquePhones = new Map<string, string>();
  phones.forEach((p) => {
    const raw = p.phoneNumber || "";
    const normalized = raw.startsWith("+") ? raw.slice(1) : raw;
    if (raw.startsWith("+") || !uniquePhones.has(normalized)) {
      uniquePhones.set(normalized, raw);
    }
  });
  return Array.from(uniquePhones.values());
};

const getPreferredPhone = (phones?: any[]) => {
  const filtered = getFilteredPhones(phones);
  return filtered.length > 0 ? filtered[0] : "";
};

const SendMessageModal = memo(
  ({
    open,
    client,
    onClose,
    onSend,
    isSending,
  }: {
    open: boolean;
    client: any;
    onClose: () => void;
    onSend: (message: string) => void;
    isSending: boolean;
  }) => {
    const [message, setMessage] = useState("");

    const handleSend = () => {
      onSend(message);
      setMessage("");
    };

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: "var(--card-bg)",
            color: "var(--text-primary)",
            borderRadius: "16px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
          },
        }}
      >
        <DialogTitle
          style={{
            borderBottom: "1px solid var(--border-color)",
            padding: "24px",
            fontSize: "20px",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <SendIcon style={{ color: "var(--accent)", fontSize: "28px" }} />
          <div>
            <div>إرسال رسالة</div>
            <div
              style={{
                fontSize: "14px",
                fontWeight: "normal",
                color: "var(--muted-text)",
                marginTop: "4px",
              }}
            >
              {client?.fullName || "العميل"}
            </div>
          </div>
        </DialogTitle>
        <DialogContent style={{ padding: "24px" }}>
          <TextField
            multiline
            rows={5}
            fullWidth
            placeholder="اكتب رسالتك هنا..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            autoFocus
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "var(--bg-page)",
                borderRadius: "12px",
                fontSize: "15px",
                "& fieldset": {
                  borderColor: "var(--border-color)",
                  borderWidth: "2px",
                },
                "&:hover fieldset": {
                  borderColor: "var(--accent)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "var(--accent)",
                  borderWidth: "2px",
                },
              },
              "& .MuiInputBase-input": {
                color: "var(--text-primary)",
              },
            }}
          />
          {getFilteredPhones(client?.phones).map((phone, idx) => (
            <div
              key={idx}
              style={{
                marginTop: "16px",
                padding: "12px 16px",
                backgroundColor: "var(--bg-page)",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                border: "1px solid var(--border-color)",
              }}
            >
              <div
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: "var(--accent)",
                }}
              />
              <span style={{ color: "var(--muted-text)", fontSize: "14px" }}>
                {idx === 0 ? "سيتم الإرسال إلى:" : "رقم إضافي:"}
              </span>
              <span
                style={{
                  color: "var(--text-primary)",
                  fontWeight: "bold",
                  direction: "ltr",
                }}
              >
                {phone}
              </span>
            </div>
          ))}
        </DialogContent>
        <DialogActions
          style={{
            padding: "16px 24px 24px",
            borderTop: "1px solid var(--border-color)",
            gap: "12px",
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderRadius: "10px",
              padding: "10px 24px",
              textTransform: "none",
              fontSize: "15px",
              fontWeight: "600",
              borderColor: "var(--border-color)",
              color: "var(--text-primary)",
              borderWidth: "2px",
              "&:hover": {
                borderColor: "var(--muted-text)",
                borderWidth: "2px",
                backgroundColor: "transparent",
              },
            }}
          >
            إلغاء
          </Button>
          <Button
            onClick={handleSend}
            disabled={!message.trim() || isSending}
            variant="contained"
            sx={{
              borderRadius: "10px",
              padding: "10px 32px",
              textTransform: "none",
              fontSize: "15px",
              fontWeight: "600",
              backgroundColor: "var(--accent)",
              color: "var(--tab-active-text)",
              boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
              "&:hover": {
                backgroundColor: "var(--accent)",
                boxShadow: "0 6px 16px rgba(59, 130, 246, 0.4)",
                transform: "translateY(-1px)",
              },
              "&:disabled": {
                backgroundColor: "var(--muted-text)",
                color: "var(--bg-page)",
              },
              transition: "all 0.2s ease",
            }}
          >
            {isSending ? (
              <span
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <span
                  style={{
                    width: "16px",
                    height: "16px",
                    border: "2px solid var(--tab-active-text)",
                    borderTopColor: "transparent",
                    borderRadius: "50%",
                    animation: "spin 0.8s linear infinite",
                  }}
                />
                جاري الإرسال...
              </span>
            ) : (
              "إرسال"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);

SendMessageModal.displayName = "SendMessageModal";

const BulkSendMessageModal = memo(
  ({
    open,
    selectedCount,
    onClose,
    onSend,
    isSending,
  }: {
    open: boolean;
    selectedCount: number;
    onClose: () => void;
    onSend: (message: string) => void;
    isSending: boolean;
  }) => {
    const [message, setMessage] = useState("");

    const handleSend = () => {
      onSend(message);
      setMessage("");
    };

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: "var(--card-bg)",
            color: "var(--text-primary)",
            borderRadius: "16px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
          },
        }}
      >
        <DialogTitle
          style={{
            borderBottom: "1px solid var(--border-color)",
            padding: "24px",
            fontSize: "20px",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <SendIcon style={{ color: "var(--accent)", fontSize: "28px" }} />
            <div>
              <div>إرسال رسالة جماعية</div>
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "normal",
                  color: "var(--muted-text)",
                  marginTop: "4px",
                }}
              >
                إلى {selectedCount} مستخدم
              </div>
            </div>
          </div>
          <div
            style={{
              backgroundColor: "var(--accent)",
              color: "var(--tab-active-text)",
              padding: "8px 16px",
              borderRadius: "20px",
              fontSize: "16px",
              fontWeight: "bold",
              boxShadow: "0 2px 8px rgba(59, 130, 246, 0.3)",
            }}
          >
            {selectedCount}
          </div>
        </DialogTitle>
        <DialogContent style={{ padding: "24px" }}>
          <TextField
            multiline
            rows={7}
            fullWidth
            placeholder="اكتب رسالتك هنا... سيتم إرسالها إلى جميع المستخدمين المحددين"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            autoFocus
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "var(--bg-page)",
                borderRadius: "12px",
                fontSize: "15px",
                "& fieldset": {
                  borderColor: "var(--border-color)",
                  borderWidth: "2px",
                },
                "&:hover fieldset": {
                  borderColor: "var(--accent)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "var(--accent)",
                  borderWidth: "2px",
                },
              },
              "& .MuiInputBase-input": {
                color: "var(--text-primary)",
              },
            }}
          />
        </DialogContent>
        <DialogActions
          style={{
            padding: "16px 24px 24px",
            borderTop: "1px solid var(--border-color)",
            gap: "12px",
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderRadius: "10px",
              padding: "10px 24px",
              textTransform: "none",
              fontSize: "15px",
              fontWeight: "600",
              borderColor: "var(--border-color)",
              color: "var(--text-primary)",
              borderWidth: "2px",
              "&:hover": {
                borderColor: "var(--muted-text)",
                borderWidth: "2px",
                backgroundColor: "transparent",
              },
            }}
          >
            إلغاء
          </Button>
          <Button
            onClick={handleSend}
            disabled={!message.trim() || isSending}
            variant="contained"
            sx={{
              borderRadius: "10px",
              padding: "10px 32px",
              textTransform: "none",
              fontSize: "15px",
              fontWeight: "600",
              backgroundColor: "var(--accent)",
              color: "var(--tab-active-text)",
              boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
              "&:hover": {
                backgroundColor: "var(--accent)",
                boxShadow: "0 6px 16px rgba(59, 130, 246, 0.4)",
                transform: "translateY(-1px)",
              },
              "&:disabled": {
                backgroundColor: "var(--muted-text)",
                color: "var(--bg-page)",
              },
              transition: "all 0.2s ease",
            }}
          >
            {isSending ? (
              <span
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <span
                  style={{
                    width: "16px",
                    height: "16px",
                    border: "2px solid var(--tab-active-text)",
                    borderTopColor: "transparent",
                    borderRadius: "50%",
                    animation: "spin 0.8s linear infinite",
                  }}
                />
                جاري الإرسال...
              </span>
            ) : (
              `إرسال إلى ${selectedCount} مستخدم`
            )}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);

BulkSendMessageModal.displayName = "BulkSendMessageModal";

const PhoneBook = () => {
  // Fetch all labels
  const { data: labelsData } = useQuery({
    queryKey: ["labels"],
    queryFn: async () => {
      const response = await api.get("/labels");
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
  const { enqueueSnackbar } = useSnackbar();
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(0);
  const [isClientNameNull, setIsClientNameNull] = useState(undefined);
  const [genderNull, setGenderNull] = useState(undefined);
  const [callIds, setCallIds] = useState([]);
  const [selectAllCallsAsGender, setSelectAllCallsAsGender] =
    useState("female");
  const [showLabelFilter, setShowLabelFilter] = useState(false);
  const [messageModal, setMessageModal] = useState(() => ({
    open: false,
    client: null,
  }));
  const [bulkMessageModal, setBulkMessageModal] = useState(false);
  const [openParticipationModal, setOpenParticipationModal] = useState(false);
  const [selectedUserInfo, setSelectedUserInfo] = useState<{
    clientId: number;
    fullName: string;
    phoneNumber: string;
  } | null>(null);
  const limit: number = 100;

  const handleSearch = (value) => {
    let cleanedPhoneNumber = convertArabicToEnglishNumber(value)?.replace(
      /^07/,
      "7"
    );
    return cleanedPhoneNumber.replace(/^(\+964|964)/, "");
  };

  const {
    data: usersData,
    refetch,
    isLoading,
  } = useQuery({
    queryKey: [
      `users-page-${page}-limit-${limit}-${search}-${isClientNameNull}-${genderNull}-${showLabelFilter}`,
    ],
    queryFn: async () => {
      let starLabelId;
      if (showLabelFilter && labelsData?.array) {
        const starLabel = labelsData.array.find(
          (label) => label.name === "star"
        );
        starLabelId = starLabel?.id;
      }
      const data = await getAllPhoneBook({
        currentPage: page,
        limit: limit,
        search: handleSearch(search),
        clientName: isClientNameNull,
        genderNull: genderNull,
        labelId: showLabelFilter ? starLabelId : undefined,
      });
      return data;
    },
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    refetch();
  }, []);

  const [editingRowIndex, setEditingRowIndex] = useState(null);
  const [editedFullName, setEditedFullName] = useState("");
  const [editedGender, setEditedGender] = useState("");
  const [editedAutoComplete, setEditedAutoComplete] = useState<any>();
  const [editedPhoneNumber, setEditedPhoneNumber] = useState<any>();
  const [editedNotes, setEditedNotes] = useState("");

  const handleDoubleClick = (index, fullName, gender, phone, notes) => {
    setEditingRowIndex(index);
    setEditedFullName(fullName);
    setEditedGender(gender);
    setEditedPhoneNumber(phone);
    setEditedNotes(notes);
  };

  const handleFullNameChange = (e) => {
    setEditedFullName(e.target.value);
  };

  const handleGenderChange = (e) => {
    setEditedGender(e.target.value);
  };

  const handleNotesChange = (e) => {
    setEditedNotes(e.target.value);
  };

  const handleSubmitData = (index, userId) => {
    api
      .patch(`${import.meta.env.VITE_SOME_KEY_BASE_URL}/clients/${userId}`, {
        fullName: editedFullName,
        neighborhoodId: editedAutoComplete.neighborhood_id ?? undefined,
        cityId: editedAutoComplete.city_id ?? undefined,
        subCityId: editedAutoComplete.sub_city_id ?? undefined,
        governorateId: editedAutoComplete.governorate_id ?? undefined,
        phoneNumber: editedPhoneNumber[0]?.phoneNumber,
        gender: editedGender,
        notes: editedNotes,
      })
      .then((response) => {
        refetch();
      });

    setEditedAutoComplete("");
    setEditingRowIndex(null);
    setEditedFullName("");
    setEditedNotes("");
  };

  const [isInteracting, setIsInteracting] = useState(false);

  const handleInteractionStart = () => setIsInteracting(true);
  const handleInteractionEnd = () => setIsInteracting(false);

  const handleClickAway = () => {
    if (
      !isInteracting &&
      editingRowIndex !== null &&
      usersData?.array?.[editingRowIndex]?.id
    ) {
      handleSubmitData(editingRowIndex, usersData.array[editingRowIndex].id);
    }
  };

  const handlePagination = useCallback(
    debounce((value) => {
      setPage(Number(value) - 1);
    }, 1000),
    []
  );

  const handleCallsCheckboxChange = (newValue) => {
    if (callIds.includes(newValue)) {
      setCallIds(callIds.filter((id) => id !== newValue));
    } else {
      setCallIds([...callIds, newValue]);
    }
  };

  const submitGenderForAllCalls = () => {
    api
      .patch(`/clients/update/gender`, {
        ids: callIds,
        gender: selectAllCallsAsGender,
      })
      .then(() => {
        refetch();
        enqueueSnackbar("تم تعديل الجنس بنجاح", { variant: "success" });
      });

    setCallIds([]);
    setSelectAllCallsAsGender("female");
  };

  const getPortByPhoneNumber = (phoneNumber: string): number => {
    const cleanNumber = phoneNumber.replace(/[\s\-\+]/g, "");

    if (/^(964)?77/.test(cleanNumber) || /^077/.test(cleanNumber)) {
      return 1;
    }
    if (/^(964)?78/.test(cleanNumber) || /^078/.test(cleanNumber)) {
      return 2;
    }
    if (/^(964)?75/.test(cleanNumber) || /^075/.test(cleanNumber)) {
      return 0;
    }

    return 1;
  };

  const { mutateAsync: sendMessages, isLoading: isSendingMessage } =
    useMutation({
      mutationFn: async ({
        messages,
      }: {
        messages: Array<{ content: string; phoneNumber: string; port: number }>;
      }) => {
        const response = await api.post("/messages/send-bulk", {
          messages: messages,
        });
        return response.data;
      },
      onSuccess: (data, variables) => {
        if (variables.messages.length === 1) {
          enqueueSnackbar("تم ارسال الرسالة بنجاح", { variant: "success" });
          setMessageModal({ open: false, client: null });
        } else {
          enqueueSnackbar("تم ارسال الرسائل بنجاح", { variant: "success" });
          setBulkMessageModal(false);
          setCallIds([]);
        }
      },
      onError: (error, variables) => {
        if (variables.messages.length === 1) {
          enqueueSnackbar("فشل في ارسال الرسالة", { variant: "error" });
        } else {
          enqueueSnackbar("فشل في ارسال الرسائل", { variant: "error" });
        }
      },
    });

  const handleOpenMessageModal = (client) => {
    if (!client || !client.id) {
      enqueueSnackbar("بيانات العميل غير متوفرة", { variant: "error" });
      return;
    }
    try {
      const safeClient = {
        id: client.id,
        fullName: client.fullName || "عميل",
        phones: Array.isArray(client.phones) ? client.phones : [],
      };

      setMessageModal({ open: true, client: safeClient });
    } catch (error) {
      console.error("Error opening modal:", error);
      enqueueSnackbar("خطأ في فتح نافذة الرسالة", { variant: "error" });
    }
  };

  const handleCloseMessageModal = () => {
    setMessageModal({ open: false, client: null });
  };

  const handleSendMessage = async (message: string) => {
    if (!message.trim() || !messageModal.client) return;

    const phones = messageModal.client.phones;
    if (!phones || !Array.isArray(phones) || phones.length === 0) {
      enqueueSnackbar("لا يوجد رقم هاتف مسجل لهذا العميل", {
        variant: "error",
      });
      return;
    }

    const phoneNumber = getPreferredPhone(phones);
    if (!phoneNumber) {
      enqueueSnackbar("رقم الهاتف غير متوفر", { variant: "error" });
      return;
    }

    try {
      await sendMessages({
        messages: [
          {
            content: message.trim(),
            phoneNumber: phoneNumber,
            port: getPortByPhoneNumber(phoneNumber),
          },
        ],
      });
    } catch (error) {
      // Error already handled in mutation
    }
  };

  const { mutateAsync: toggleClientLabel } = useMutation({
    mutationFn: async ({
      clientId,
      labelId,
      action,
    }: {
      clientId: number;
      labelId: number;
      action: "add" | "remove";
    }) => {
      if (action === "add") {
        const response = await api.post("/client-labels", {
          clientId,
          labelId,
        });
        return response.data;
      } else {
        const response = await api.delete(
          `/client-labels/${clientId}/${labelId}`
        );
        return response.data;
      }
    },
    onSuccess: () => {
      refetch();
    },
    onError: (error) => {
      console.error("Mutation failed:", error);
    },
  });

  const toggleStar = async (clientId: number, isCurrentlyStarred: boolean) => {
    try {
      if (!labelsData?.array || labelsData.array.length === 0) {
        console.error("Labels data not loaded yet or empty");
        return;
      }

      const starLabel =
        labelsData.array.find((label) => label.name === "star") ||
        labelsData.array[0];

      if (!starLabel) {
        console.error("No labels available");
        return;
      }

      const action = isCurrentlyStarred ? "remove" : "add";

      await toggleClientLabel({
        clientId: clientId,
        labelId: starLabel.id,
        action: action,
      });
    } catch (error) {}
  };

  const handleSendBulkMessage = async (message: string) => {
    if (!message.trim() || callIds.length === 0) return;

    const selectedUsers = usersData?.array?.filter((user) =>
      callIds.includes(user.id)
    );

    if (!selectedUsers || selectedUsers.length === 0) {
      enqueueSnackbar("لم يتم اختيار أي مستخدم", { variant: "error" });
      return;
    }

    const messages = selectedUsers
      .filter(
        (user) =>
          user.phones &&
          Array.isArray(user.phones) &&
          user.phones.length > 0 &&
          getPreferredPhone(user.phones)
      )
      .map((user) => ({
        content: message.trim(),
        phoneNumber: getPreferredPhone(user.phones),
        port: getPortByPhoneNumber(getPreferredPhone(user.phones)),
      }));

    if (messages.length === 0) {
      enqueueSnackbar("لا يوجد أرقام هاتف صالحة للمستخدمين المحددين", {
        variant: "error",
      });
      return;
    }

    try {
      await sendMessages({ messages });
    } catch (error) {
      // Error already handled in mutation
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "var(--bg-page)",
        padding: "20px 40px",
        color: "var(--text-primary)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          gap: "10px",
          alignItems: "center",
        }}
      >
        <CustomTextField
          placeholder="البحث"
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            direction: "rtl",
            textAlign: "left",
            backgroundColor: "var(--card-bg)",
            color: "var(--text-primary)",
          }}
          disabled={isClientNameNull}
        />
        <Button
          sx={{
            backgroundColor:
              isClientNameNull === undefined
                ? "var(--card-bg)"
                : "var(--accent)",
            color:
              isClientNameNull === undefined
                ? "var(--accent)"
                : "var(--tab-active-text)",
            borderRadius: "6px",
            "&:hover": {
              backgroundColor:
                isClientNameNull === undefined
                  ? "var(--card-bg)"
                  : "var(--accent)",
              color:
                isClientNameNull === undefined
                  ? "var(--accent)"
                  : "var(--tab-active-text)",
            },
          }}
          onClick={() => {
            if (isClientNameNull === undefined) {
              setIsClientNameNull("name_null");
            } else {
              setIsClientNameNull(undefined);
            }
          }}
        >
          بدون اسماء
        </Button>
        <Button
          sx={{
            backgroundColor:
              genderNull === undefined ? "var(--card-bg)" : "var(--accent)",
            color:
              genderNull === undefined
                ? "var(--accent)"
                : "var(--tab-active-text)",
            borderRadius: "6px",
            "&:hover": {
              backgroundColor:
                genderNull === undefined ? "var(--card-bg)" : "var(--accent)",
              color:
                genderNull === undefined
                  ? "var(--accent)"
                  : "var(--tab-active-text)",
            },
          }}
          onClick={() => {
            if (genderNull === undefined) {
              setGenderNull("gender_null");
            } else {
              setGenderNull(undefined);
            }
          }}
        >
          بدون جنس
        </Button>
        <Button
          sx={{
            backgroundColor:
              showLabelFilter === false ? "var(--card-bg)" : "var(--accent)",
            color:
              showLabelFilter === false
                ? "var(--accent)"
                : "var(--tab-active-text)",
            borderRadius: "6px",
            padding: "5px 20px",
            textTransform: "none",
            "&:hover": {
              backgroundColor:
                showLabelFilter === false ? "var(--card-bg)" : "var(--accent)",
              color:
                showLabelFilter === false
                  ? "var(--accent)"
                  : "var(--tab-active-text)",
            },
          }}
          onClick={() => setShowLabelFilter((prev) => !prev)}
        >
          ليبل
        </Button>
        {callIds?.length > 0 && (
          <>
            <Box
              sx={{ border: "1px solid var(--accent)", borderRadius: "6px" }}
            >
              <Button
                sx={{
                  padding: "5px 20px",
                  textTransform: "none",
                  backgroundColor:
                    selectAllCallsAsGender === "male" ? "var(--accent)" : "",
                  color:
                    selectAllCallsAsGender === "male"
                      ? "var(--tab-active-text)"
                      : "",
                  "&:hover": {
                    backgroundColor:
                      selectAllCallsAsGender === "male" ? "var(--accent)" : "",
                    color:
                      selectAllCallsAsGender === "male"
                        ? "var(--tab-active-text)"
                        : "",
                  },
                }}
                onClick={() => setSelectAllCallsAsGender("male")}
              >
                ذكور
              </Button>
              <Button
                sx={{
                  padding: "5px 20px",
                  textTransform: "none",
                  backgroundColor:
                    selectAllCallsAsGender === "female" ? "var(--accent)" : "",
                  color:
                    selectAllCallsAsGender === "female"
                      ? "var(--tab-active-text)"
                      : "",

                  "&:hover": {
                    backgroundColor:
                      selectAllCallsAsGender === "female"
                        ? "var(--accent)"
                        : "",
                    color:
                      selectAllCallsAsGender === "female"
                        ? "var(--tab-active-text)"
                        : "",
                  },
                }}
                onClick={() => setSelectAllCallsAsGender("female")}
              >
                اناث
              </Button>
            </Box>
            <Button
              sx={{
                padding: "5px 20px",
                textTransform: "none",
                backgroundColor: "var(--card-bg)",
                color: "var(--accent)",
                "&:hover": {
                  backgroundColor: "var(--card-bg)",
                  color: "var(--accent)",
                },
              }}
              onClick={() => submitGenderForAllCalls()}
            >
              اختبار الكل
            </Button>
            <Button
              sx={{
                padding: "5px 20px",
                textTransform: "none",
                backgroundColor: "var(--accent)",
                color: "var(--tab-active-text)",
                "&:hover": {
                  backgroundColor: "var(--accent)",
                  color: "var(--tab-active-text)",
                },
              }}
              onClick={() => setBulkMessageModal(true)}
            >
              إرسال رسالة جماعية ({callIds.length})
            </Button>
          </>
        )}
      </Box>
      <div style={{ backgroundColor: "var(--card-bg)", borderRadius: "12px" }}>
        <div
          style={{ width: "100%", marginTop: "20px", paddingBottom: "30px" }}
        >
          <TableContainer
            sx={{
              width: "100%",
              borderRadius: "6px",
            }}
          >
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead sx={{ backgroundColor: "var(--table-head-bg)" }}>
                <TableRow>
                  <TableCell>
                    <Checkbox
                      checked={callIds.length === usersData?.array?.length}
                      onChange={() => {
                        if (callIds.length === usersData?.array?.length) {
                          setCallIds([]);
                        } else {
                          setCallIds(usersData?.array?.map((user) => user.id));
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>ت</TableCell>
                  <TableCell>ليبل</TableCell>

                  <TableCell>الاسم</TableCell>
                  <TableCell>الجنس</TableCell>
                  <TableCell>رقم الهاتف</TableCell>
                  <TableCell>المنطقة</TableCell>
                  <TableCell>وقت الاتصال</TableCell>
                  <TableCell>مللاحظات</TableCell>
                  <TableCell>تعديل</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usersData?.array?.map((row, index) => (
                  <ClickAwayListener key={row.id} onClickAway={handleClickAway}>
                    <TableRow
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        tr: { cursor: "pointer" },
                      }}
                      onDoubleClick={() =>
                        handleDoubleClick(
                          index,
                          row.fullName,
                          row.gender,
                          row.phones,
                          row.notes
                        )
                      }
                    >
                      <TableCell component="th" scope="row">
                        <Checkbox
                          checked={callIds.includes(row?.id)}
                          onChange={() => handleCallsCheckboxChange(row?.id)}
                        />
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {index + 1}
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        sx={{ width: "5%" }}
                      >
                        {(() => {
                          const isStarred =
                            Array.isArray(row.clientLabels) &&
                            row.clientLabels.length > 0 &&
                            row.clientLabels.some(
                              (labelObj) => labelObj.label?.name === "star"
                            );
                          return (
                            <IconButton
                              onClick={() => toggleStar(Number(row.id), isStarred)}
                              disabled={!labelsData?.array}
                              sx={{
                                padding: "4px",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                  transform: "scale(1.1)",
                                },
                                "&:disabled": {
                                  opacity: 0.5,
                                },
                              }}
                            >
                              {isStarred ? (
                                <StarIcon
                                  sx={{
                                    color: "var(--star-active)",
                                    fontSize: "24px",
                                    filter:
                                      "drop-shadow(0 0 3px rgba(255, 215, 0, 0.5))",
                                  }}
                                />
                              ) : (
                                <StarBorderIcon
                                  sx={{
                                    color: "var(--star-inactive)",
                                    fontSize: "24px",
                                    "&:hover": {
                                      color: "var(--star-active)",
                                    },
                                  }}
                                />
                              )}
                            </IconButton>
                          );
                        })()}
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        sx={{ width: "20%" }}
                      >
                        {editingRowIndex === index ? (
                          <TextField
                            type="text"
                            value={editedFullName}
                            onChange={handleFullNameChange}
                            size="small"
                            onFocus={handleInteractionStart} // Track interaction
                            onBlur={handleInteractionEnd} // Track interaction end
                          />
                        ) : (
                          <span>{row.fullName}</span>
                        )}
                      </TableCell>
                      <TableCell component="th" sx={{ width: "5%" }}>
                        {editingRowIndex === index ? (
                          <Select
                            value={editedGender}
                            onChange={handleGenderChange}
                            label="Gender"
                            size="small"
                            onOpen={handleInteractionStart} // Dropdown opened
                            onClose={handleInteractionEnd} // Dropdown closed
                          >
                            <MenuItem value="male">ذكر</MenuItem>
                            <MenuItem value="female">انثى</MenuItem>
                          </Select>
                        ) : (
                          <>
                            {row.gender === "male" ? (
                              <Tooltip title="ذكر">
                                <ManIcon
                                  sx={{
                                    backgroundColor: "#629FC1",
                                    color: "white",
                                    borderRadius: "4px",
                                  }}
                                />
                              </Tooltip>
                            ) : row.gender === "female" ? (
                              <Tooltip title="انثى">
                                <WomanIcon
                                  sx={{
                                    backgroundColor: "#BF62C1",
                                    color: "white",
                                    borderRadius: "4px",
                                  }}
                                />
                              </Tooltip>
                            ) : (
                              <Tooltip title="غير محدد">
                                <HelpCenterIcon
                                  sx={{
                                    backgroundColor: "#555555",
                                    color: "white",
                                    borderRadius: "4px",
                                  }}
                                />
                              </Tooltip>
                            )}
                          </>
                        )}
                      </TableCell>
                      <TableCell component="th">
                        {getFilteredPhones(row?.phones).map((phone, idx) => (
                          <div key={idx} style={{ direction: "ltr" }}>
                            {phone}
                          </div>
                        ))}
                      </TableCell>
                      <TableCell component="th" sx={{ width: "20%" }}>
                        {editingRowIndex === index ? (
                          <ClientsAutoComplete
                            data={row}
                            editedAutoComplete={editedAutoComplete}
                            setEditedAutoComplete={setEditedAutoComplete}
                            locations={usersData?.locations}
                            handleInteractionStart={handleInteractionStart}
                            handleInteractionEnd={handleInteractionEnd}
                          />
                        ) : (
                          <>
                            {usersData?.locations?.[row?.id]
                              ? usersData.locations[row.id].neighborhood_name ??
                              usersData.locations[row.id].sub_city_name ??
                              usersData.locations[row.id].city_name ??
                              "لا يوجد"
                              : "لا يوجد"}
                          </>
                        )}
                      </TableCell>
                      <TableCell component="th" sx={{ width: "15%" }}>
                        {formatDateAndTime(row.createdAt)}
                      </TableCell>
                      <TableCell component="th" sx={{ width: "100%" }}>
                        {editingRowIndex === index ? (
                          <TextField
                            multiline
                            sx={{ width: "100%" }}
                            value={editedNotes}
                            onChange={handleNotesChange}
                            size="small"
                            onFocus={handleInteractionStart} // Track interaction
                            onBlur={handleInteractionEnd} // Track interaction end
                          />
                        ) : (
                          <span>{row.notes}</span>
                        )}
                      </TableCell>
                      {editingRowIndex === index ? (
                        <TableCell>
                          <Button
                            onClick={() => handleSubmitData(index, row.id)}
                          >
                            حفظ
                          </Button>
                        </TableCell>
                      ) : (
                        <TableCell>
                          <Box sx={{ display: "flex", gap: "8px" }}>
                            <Tooltip title="تعديل البيانات">
                              <IconButton
                                onClick={() =>
                                  handleDoubleClick(
                                    index,
                                    row.fullName,
                                    row.gender,
                                    row.phones,
                                    row.notes
                                  )
                                }
                                sx={{
                                  color: "var(--text-primary)",
                                  "&:hover": {
                                    backgroundColor: "var(--table-head-bg)",
                                  },
                                }}
                              >
                                <EditOutlinedIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="إرسال رسالة">
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenMessageModal(row);
                                }}
                                sx={{
                                  color: "var(--accent)",
                                  "&:hover": {
                                    backgroundColor: "var(--table-head-bg)",
                                  },
                                }}
                              >
                                <SendIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="إضافة جائزة">
                              <IconButton
                                onClick={() => {
                                  setSelectedUserInfo({
                                    clientId: Number(row.id),
                                    fullName: row.fullName || "Unknown",
                                    phoneNumber:
                                      getPreferredPhone(row.phones),
                                  });
                                  setOpenParticipationModal(true);
                                }}
                                color="success"
                                size="small"
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "var(--table-head-bg)",
                                  },
                                }}
                              >
                                <CardGiftcardIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  </ClickAwayListener>
                ))}
              </TableBody>
            </Table>
            {isLoading && (
              <LinearProgress
                color="primary"
                data-testid="product-member-loader"
              />
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginTop: "10px",
                gap: "10px",
              }}
            >
              <Pagination
                sx={{ marginLeft: "20px" }}
                page={page + 1}
                count={Math.ceil(usersData?.count / limit)}
                onChange={(e, value) => setPage(value - 1)}
                variant="outlined"
                shape="rounded"
                size="medium"
              />
              <TextField
                type="number"
                onChange={(e) => {
                  if (
                    Number(e.target.value) > 0 &&
                    Number(e.target.value) <=
                    Math.ceil(usersData?.count / limit)
                  ) {
                    handlePagination(Number(e.target.value));
                  }
                }}
                defaultValue={page + 1}
                size="small"
                sx={{ width: "100px" }}
              />
            </Box>
          </TableContainer>
        </div>
      </div>

      {messageModal?.open === true && messageModal.client !== null && (
        <SendMessageModal
          open={true}
          client={messageModal.client}
          onClose={handleCloseMessageModal}
          onSend={handleSendMessage}
          isSending={isSendingMessage}
        />
      )}

      {bulkMessageModal && (
        <BulkSendMessageModal
          open={true}
          selectedCount={callIds.length}
          onClose={() => setBulkMessageModal(false)}
          onSend={handleSendBulkMessage}
          isSending={isSendingMessage}
        />
      )}

      {selectedUserInfo && (
        <AddParticipationModal
          open={openParticipationModal}
          onClose={() => setOpenParticipationModal(false)}
          onSuccess={() => refetch()}
          userInfo={selectedUserInfo}
          modalType="bonus"
        />
      )}
    </div>
  );
};

export default PhoneBook;
